// --- Community Food Enterprise guide (owner brief, 27 October 2026) ----------
// Australia-wide educational and project-development resource. All data is
// separated from layout for maintenance. No invented sponsors, programmes,
// launch dates, funding commitments or unverified fees/thresholds.

export const cfeHero = {
  eyebrow: "Community food systems",
  heading: "Build a food enterprise that serves your community.",
  intro:
    "Practical pathways to establish, fund and sustain a community food enterprise - bringing together affordable food, fair livelihoods, local ownership and resilient food systems.",
  supporting:
    "Start with a real community need. Test a workable model. Build the governance and funding that allow it to last.",
  primaryCta: { label: "Explore establishment pathways", anchor: "choose-a-pathway" },
  secondaryCta: { label: "Explore funding options", anchor: "funding" },
};

export const cfeOverview = {
  definition:
    "A community food enterprise produces, prepares, distributes or sells food to create a defined community benefit. It may operate as a co-operative, not-for-profit, social enterprise, Indigenous-controlled organisation or partnership. The right model depends on who it serves, who owns it, how decisions are made and how ongoing costs are covered.",
  realityCheck:
    "A good mission does not replace demand, fair wages, safe food handling or a workable budget. If affordable food requires an ongoing subsidy, identify that subsidy openly and plan how it will be maintained.",
  questions: [
    "Who benefits and who decides?",
    "What will the enterprise sell or deliver, and to whom?",
    "What pays for establishment, ongoing operations and any deliberate affordability subsidy?",
  ],
  notEveryNote:
    "Not every community food initiative needs to become a business. A volunteer mutual-aid activity, community garden or subsidised service may be the appropriate model. This guide helps you choose deliberately.",
};

export const cfeNav = [
  { label: "Overview", anchor: "overview" },
  { label: "Choose a pathway", anchor: "choose-a-pathway" },
  { label: "Establish", anchor: "establish" },
  { label: "Governance", anchor: "governance" },
  { label: "Compliance", anchor: "compliance" },
  { label: "Funding", anchor: "funding" },
  { label: "Check the numbers", anchor: "check-the-numbers" },
  { label: "Readiness", anchor: "readiness" },
  { label: "Resources", anchor: "resources" },
];

// --- Section 5: enterprise pathways ------------------------------------------

export const cfePathwayPrompts = [
  "What need does this address?",
  "Who would pay?",
  "Who delivers it?",
  "What is the smallest responsible test?",
  "What would make us stop or redesign?",
];

export const cfePathways = [
  {
    name: "Community buying group / food co-op",
    activity: "Aggregate household demand and purchase food collectively.",
    income: "Membership, handling margin, subscriptions.",
    pilot: "One pre-ordered collection cycle.",
    issues: "Demand, minimum orders, storage, collection, member participation, affordability.",
  },
  {
    name: "Grower-led distribution hub",
    activity: "Aggregate local produce for households or institutional buyers.",
    income: "Sales margin, distribution fees, supply contracts.",
    pilot: "A small product range with confirmed buyers.",
    issues: "Supply reliability, cold chain, freight, grading, working capital, producer payment terms.",
  },
  {
    name: "Community-supported agriculture",
    activity: "Households commit to a season or regular supply.",
    income: "Seasonal shares, subscriptions, produce sales.",
    pilot: "A limited subscription offer matched to realistic production.",
    issues: "Crop risk, seasonality, customer expectations, land, water, labour, refund terms.",
  },
  {
    name: "Market garden / community farm",
    activity: "Produce food locally for sale and community benefit.",
    income: "Produce sales, subscriptions, appropriate training or service contracts.",
    pilot: "A defined growing area with a confirmed outlet.",
    issues: "Secure land and water access, soil, climate, labour, yields, contamination, biosecurity.",
  },
  {
    name: "Community kitchen / catering enterprise",
    activity: "Prepare meals, catering or products.",
    income: "Catering, meal sales, subscriptions, delivery contracts.",
    pilot: "A costed menu delivered from an appropriately approved venue.",
    issues: "Kitchen access, food safety, allergens, labour, transport, customer demand.",
  },
  {
    name: "Affordable pantry / grocery",
    activity: "Improve access to food at accessible prices.",
    income: "Sales, membership, committed subsidy or contracted services.",
    pilot: "A small, regularly reviewed pantry offer.",
    issues: "Food supply, dignity, stock losses, pricing, cold storage, subsidy reliability.",
  },
  {
    name: "Surplus food recovery and redistribution",
    activity: "Recover suitable food and redistribute safely.",
    income: "Service contracts, grants, donations, permitted complementary trading.",
    pilot: "One agreed source and one safe distribution route.",
    issues: "Food suitability, traceability, transport, temperature, insurance, consistency of supply.",
  },
  {
    name: "Value-added local food enterprise",
    activity: "Turn suitable produce into products.",
    income: "Product sales, wholesale, contract production.",
    pilot: "A small validated batch and sales test.",
    issues: "Shelf life, labelling, production costs, processing rules, market access, recall procedures.",
  },
  {
    name: "Training and employment enterprise",
    activity: "Use food activity to create employment or learning outcomes.",
    income: "Trading plus explicitly agreed service or training funding.",
    pilot: "A small supported cohort with qualified partners.",
    issues: "Supervision, wage obligations, support costs, safeguarding, training accreditation if relevant.",
  },
  {
    name: "First Nations-led food enterprise",
    activity: "Develop a food activity under the authority of relevant First Nations owners and knowledge holders.",
    income: "Trading, appropriate partnerships, grants or patient finance.",
    pilot: "A community-authorised pilot with clear ownership and benefit arrangements.",
    issues: "Cultural authority, consent, Country access, knowledge protection, ecological limits, commercial rights.",
  },
];

export const cfeCoopNote =
  "'Food co-op' can refer to an activity or to a registered co-operative. The activity name does not itself establish legal status.";

// --- Section 6: establishment roadmap ----------------------------------------

export const cfeRoadmapIntro =
  "Stages with evidence-based gates, not a fixed 90-day launch guarantee. Any early planning sequence is illustrative and adaptable to your situation.";

export const cfeRoadmap = [
  {
    letter: "A",
    title: "Understand the need and existing food system",
    body: "Map residents, growers, retailers, food relief, community organisations, Traditional Owners, transport, existing infrastructure and potential buyers. Identify unmet need and avoid displacing effective local organisations. Distinguish interest from purchase commitments. Ask about cultural suitability, disability access, transport, price, collection times and payment options.",
    outputs: [
      "Short community needs statement",
      "Stakeholder map",
      "Evidence summary",
      "Existing-service scan",
      "Initial definition of community benefit",
    ],
    gate: "Gate: there is a specific need, a constituency who wants the response and a reason this model adds value.",
  },
  {
    letter: "B",
    title: "Form the organising group",
    body: "Agree purpose, boundaries, time commitments and decision making. Identify delivery, finance, governance, community engagement and food operations skills. Determine who has authority to speak for whom. Identify conflicts of interest and founder dependence.",
    outputs: [
      "Working group roles",
      "Interim decision process",
      "Meeting record",
      "Skills gaps",
      "Engagement plan",
    ],
    gate: "Gate: the project has accountable people with realistic capacity, not just one exhausted founder.",
  },
  {
    letter: "C",
    title: "Test the offer and economics",
    body: "Select one initial offer. Interview intended customers and suppliers. Where lawful and safe, test pre-orders, a collection cycle, a small catering offer or a limited growing plan. Include true labour, waste, transport and administration costs. Test repeat demand, not merely a successful launch event.",
    outputs: [
      "Pilot plan",
      "Pricing assumptions",
      "Customer evidence",
      "Supplier terms",
      "Cost per unit",
      "Initial risk register",
    ],
    gate: "Gate: demand and contribution margin are credible, or a deliberate service subsidy has a credible funding route.",
  },
  {
    letter: "D",
    title: "Choose governance and legal structure",
    body: "Choose the ownership model before incorporating. Compare appropriate structures and hosting options. Obtain advice where complexity or risk requires it. Establish the appropriate registrations, bank arrangements, financial controls and governing documents. There is no universal registration sequence - it depends on the structure and jurisdiction.",
    outputs: [
      "Structure decision record",
      "Ownership and membership rules",
      "Governance documents",
      "Registration checklist",
      "Delegated authorities",
    ],
    gate: "Gate: the entity or host can lawfully undertake the proposed activity and accept the proposed funding.",
  },
  {
    letter: "E",
    title: "Confirm premises and operational requirements",
    body: "Before entering a lease or buying equipment, check permitted use, food regulatory requirements, accessibility, utilities, waste, refrigeration, transport, maintenance, storage and insurance. Verify permission to operate independently in a shared kitchen. Farming may involve land tenure, water, primary production, biosecurity and environmental requirements beyond retail food rules.",
    outputs: [
      "Location-specific compliance map",
      "Premises agreement",
      "Operating procedures",
      "Insurance advice",
      "Staff and volunteer plan",
      "Supplier and customer agreements",
    ],
    gate: "Gate: the proposed operation is safe, permissible and costed.",
  },
  {
    letter: "F",
    title: "Assemble funding and working capital",
    body: "Separate establishment expenditure, assets, ongoing operating costs and affordability subsidy. Forecast monthly cash flow, including payment delays, seasonal supply, customer prepayments, restricted grants and producer payments. Identify what happens if the grant is late or sales disappoint.",
    outputs: [
      "Funding mix",
      "Eligible use of each source",
      "Cash-flow forecast",
      "Quotes",
      "Contingency",
      "Documented capital contributions or loans",
      "Funder-ready proposal",
    ],
    gate: "Gate: there is sufficient available cash to launch responsibly, not merely a list of applications awaiting decisions.",
  },
  {
    letter: "G",
    title: "Pilot, measure, decide",
    body: "Launch within a deliberately limited scope. Track trading, community outcomes, food safety, staff capacity and cash. Hold a review with the community and governing body. Decide to continue, adapt, grow, pause or stop against pre-agreed criteria.",
    outputs: [
      "Pilot report",
      "Updated budget",
      "Customer feedback",
      "Governance review",
      "Next-stage decision",
    ],
    gate: "Gate: expansion follows evidence and delivery capacity.",
  },
];

// --- Section 7: governance and legal pathways --------------------------------

export const cfeGovernanceIntro =
  "A comparison of options, not personalised legal recommendations. Obtain advice where complexity or risk requires it.";

export const cfeGovernanceOptions = [
  {
    option: "Auspiced or hosted pilot",
    fit: "Test an idea through an established organisation.",
    checks:
      "Written agreement on purpose, control, funds, fees, employment, insurance, assets, reporting, liabilities and exit. Host eligibility does not automatically transfer to the project.",
  },
  {
    option: "Incorporated association",
    fit: "Membership-based not-for-profit community activity.",
    checks:
      "State or territory requirements, permitted activity, committee capacity, reporting, operations beyond the home jurisdiction and governing rules.",
  },
  {
    option: "Registered co-operative",
    fit: "Member ownership and democratic participation.",
    checks:
      "Relevant law and registration authority; active membership; distributing or non-distributing model; member rights, capital, board responsibilities and permitted surplus treatment.",
  },
  {
    option: "Company limited by guarantee",
    fit: "Some not-for-profit organisations seeking a company structure.",
    checks:
      "Governance capacity, ASIC obligations, member liability, purpose and non-distribution provisions, and charity eligibility if sought.",
  },
  {
    option: "Proprietary company",
    fit: "Commercial ownership or an appropriate investment model.",
    checks:
      "Share ownership, mission protection, investor rights, distributions, tax, liability and compatibility with target funding.",
  },
  {
    option: "Indigenous corporation",
    fit: "Some eligible Aboriginal and Torres Strait Islander groups seeking an appropriate corporation structure.",
    checks:
      "Community preference, eligibility, ORIC/CATSI requirements, governance suitability and cultural authority. Do not assume this is the only First Nations pathway.",
  },
  {
    option: "Sole trader or partnership",
    fit: "Some small commercial tests where appropriate.",
    checks:
      "Personal liability, shared obligations, ownership, insurance and fit with collective aims. Not automatically appropriate for community ownership.",
  },
];

export const cfeLegalDistinctions = [
  "Social enterprise describes a purpose and way of operating. It is not a single legal structure.",
  "Not-for-profit status, registered charity status, tax concessions and DGR endorsement are separate matters.",
];

export const cfeGovernanceQuestions = [
  "Who can join?",
  "Who votes?",
  "Who owns assets?",
  "Who approves debt?",
  "Who can sign contracts?",
  "What is paid work?",
  "How are conflicts handled?",
  "Where does surplus go?",
  "How does a member leave?",
  "What happens on closure?",
  "How can community members challenge decisions?",
  "How will the enterprise remain accountable as it grows?",
];

export const cfeIndigenousNote =
  "For Indigenous foods, seeds and knowledge, authority and consent must come from the appropriate custodians, not simply from the project founder. Address data governance, recording, branding, provenance, benefit sharing and limits on disclosure. Registering a seed or variety does not by itself create ownership of a species, establish intellectual property rights or prevent biopiracy.";

// --- Section 8: Australian compliance map ------------------------------------

export const cfeCompliance = [
  {
    area: "Business and entity registrations",
    detail: "Confirm relevant ABN, business name, entity and regulator requirements.",
  },
  {
    area: "Tax and accounting",
    detail:
      "Obtain advice on income tax, GST treatment of different food supplies, registration, payroll, superannuation, record keeping and reporting. Not all food is GST-free.",
  },
  {
    area: "Food safety",
    detail:
      "Use national standards plus the applicable regulator. Check notification, registration or licensing, food safety supervisors, food-handler training, food safety programmes where required, allergens, labelling, traceability, temperature controls and recall processes.",
  },
  {
    area: "Premises and planning",
    detail:
      "Check land use, development and building requirements, lease or licence terms, shared kitchens, waste, water, accessibility and local approvals.",
  },
  {
    area: "Primary production and processing",
    detail:
      "Check sector-specific rules, accreditation and biosecurity where applicable, especially for higher-risk products.",
  },
  {
    area: "People",
    detail:
      "Identify applicable employment arrangements, awards and conditions, contractor classification, superannuation, workers compensation and relevant workplace safety requirements. A community purpose does not remove wage obligations. Check volunteering arrangements on their facts.",
  },
  {
    area: "Insurance",
    detail:
      "Obtain advice on actual activities, including public and product liability, assets, vehicles, workers and volunteers. Cover does not replace safe operations.",
  },
  {
    area: "Contracts",
    detail:
      "Cover suppliers, growers, host venues, buyers, delivery partners, prepayments, refunds, produce quality, cancellations, ownership and dispute processes.",
  },
  {
    area: "Fundraising",
    detail:
      "Check applicable charitable fundraising and consumer requirements, platform terms, sponsorship obligations and gift claims.",
  },
  {
    area: "Investment and member finance",
    detail:
      "Obtain advice before publicly soliciting loans, offering returns, issuing shares or running investment crowdfunding.",
  },
  {
    area: "Cultural and ecological safeguards",
    detail:
      "Confirm authority, consent, sustainable harvest, Country access and knowledge-use agreements where relevant.",
  },
];

export const cfeNationalDistinction =
  "Food Standards Australia New Zealand sets relevant national food standards; operational regulation and enforcement involve state, territory and local authorities. The Australian Business Licence and Information Service (ABLIS) is a starting point for location-specific requirements. Notification and licensing are different concepts - an exemption from licensing is not an exemption from food safety obligations.";

// --- Section 9: funding --------------------------------------------------------

export const cfeFundingPrinciple =
  "Ask 'What money is needed, when, for what purpose, and what obligations come with it?' - not 'Which grant can we get?'";

export const cfeFundingSources = [
  {
    source: "Trading revenue",
    use: "Repeat operating expenditure and surplus.",
    prepare: "Tested offer, customers, pricing and delivery capability.",
    obligations: "Demand and margin must be real; cash receipts may lag costs.",
  },
  {
    source: "Customer pre-orders / subscriptions",
    use: "Test demand and support short-cycle working capital.",
    prepare: "Clear product, timing, fulfilment and refund terms.",
    obligations: "Prepayments are delivery obligations, not free capital.",
  },
  {
    source: "Membership fees",
    use: "Shared services and member participation.",
    prepare: "Membership benefits, rules and realistic uptake.",
    obligations: "Fees alone may not cover operations; do not confuse fees with equity.",
  },
  {
    source: "Member capital or member loans",
    use: "Appropriate infrastructure or working capital.",
    prepare: "Legal advice, rules, risk disclosures, terms and approvals.",
    obligations: "Not universally available; rights, repayment and capital restrictions must be clear.",
  },
  {
    source: "Community donations",
    use: "Defined public-benefit activities.",
    prepare: "Credible purpose, transparent use and reporting.",
    obligations: "DGR tax deductibility cannot be assumed; check fundraising requirements.",
  },
  {
    source: "Reward crowdfunding",
    use: "A bounded project or pre-sale campaign.",
    prepare: "Audience, campaign costs, reward costing and fulfilment.",
    obligations: "Platform fees, tax treatment, failed campaigns and undelivered rewards.",
  },
  {
    source: "Council and state/territory grants",
    use: "Locally eligible projects, equipment or community outcomes.",
    prepare: "Eligibility, local evidence, budget and approvals.",
    obligations: "Geography, entity restrictions, matched funds and excluded costs vary.",
  },
  {
    source: "Commonwealth grants",
    use: "Eligible programme outcomes.",
    prepare: "Exact programme fit, evidence and capacity.",
    obligations: "Competitive and time-bound; do not assume core operating support.",
  },
  {
    source: "Philanthropic foundations",
    use: "Charitable or public-benefit work aligned to funder priorities.",
    prepare: "Need, outcomes, governance, budget and appropriate entity or auspice.",
    obligations: "Some require charity or DGR status; grants can be restricted and temporary.",
  },
  {
    source: "Corporate sponsorship",
    use: "A defined local activity or supported programme.",
    prepare: "Sponsor proposition, deliverables and values fit.",
    obligations: "Branding obligations and conflicts; sponsorship differs from a gift.",
  },
  {
    source: "Institutional supply contracts",
    use: "Predictable demand from suitable buyers.",
    prepare: "Quality, volumes, pricing, delivery, insurance and invoicing.",
    obligations: "Tender and procurement requirements, penalties, payment delays and capacity.",
  },
  {
    source: "Service delivery agreements",
    use: "Explicitly funded access, training or community services.",
    prepare: "Costed outcomes, eligible delivery partner and reporting.",
    obligations: "Trading contracts and service subsidies should be tracked distinctly.",
  },
  {
    source: "In-kind support",
    use: "Space, equipment, transport, skills or land access.",
    prepare: "Written permissions, valuation and operating arrangements.",
    obligations: "Not cash; maintenance, utilities, insurance and withdrawal risk remain.",
  },
  {
    source: "Loans / equipment finance",
    use: "Assets or working capital backed by repayment capacity.",
    prepare: "Cash flow, security, quotes, downside case and repayment plan.",
    obligations: "Interest, fees, covenants, guarantees and debt servicing.",
  },
  {
    source: "Impact investment",
    use: "An investable model aligned with community outcomes.",
    prepare: "Governance, financial model, evidence, impact measures and terms.",
    obligations: "Investors may expect returns, control or an exit; preserve mission deliberately.",
  },
  {
    source: "Blended funding",
    use: "Separate commercial and public-benefit components.",
    prepare: "Clear uses, compatible terms, cost allocation and reporting.",
    obligations: "Avoid double funding the same cost and incompatible restrictions.",
  },
];

export const cfeFundingStages = [
  {
    stage: "Idea / discovery",
    text: "Voluntary organising within capacity, small permitted contributions, suitable in-kind support or an eligible host. Seek discovery or planning support where available; do not buy infrastructure before proving the need.",
  },
  {
    stage: "Pilot",
    text: "Small grants, permitted pre-orders, membership, donated space or an auspiced project. Fund a bounded test with clear outputs and review criteria.",
  },
  {
    stage: "Early operation",
    text: "Repeat sales, committed subsidy for access outcomes, suitable supply or service contracts and adequate working capital.",
  },
  {
    stage: "Proven expansion",
    text: "Retained surplus, appropriate grants, loans, member capital or impact investment after testing repayment and delivery capacity.",
  },
];

export const cfeFundingCategories = [
  "Food access and poverty relief",
  "Local economic development",
  "Community resilience",
  "Regional development",
  "Waste reduction and circular economy",
  "Appropriate environmental outcomes",
  "Employment and skills",
  "Eligible First Nations enterprise development",
  "Community infrastructure",
  "Health and wellbeing",
  "Local agriculture and supply chains",
];

export const cfeFundingCategoriesNote =
  "A project must meet the funder's actual criteria. Do not relabel an activity simply to chase money.";

export const cfeReadinessPackage = [
  "Concise project summary",
  "Evidence of need",
  "Customer and supplier validation",
  "Entity or auspice details",
  "Governance",
  "Budget and quotes",
  "Monthly cash flow",
  "Funding mix and gap",
  "Project milestones",
  "Staffing and delivery capacity",
  "Permissions",
  "Risk register",
  "Impact measures",
  "Genuine partner letters",
  "Reporting approach",
  "Continuation or responsible exit plan",
];

// Curated register. Verification dates are maintained by hand, independently of
// builds. No amounts or deadlines unless verified against an open round.
export const cfeFundingRegister = {
  verified: "27 October 2026",
  note: "A modest curated register, not a comprehensive database. A listing is not an offer, and an existing webpage does not mean a round is open. Check current availability on the official page.",
  entries: [
    {
      name: "Grants and programs finder",
      provider: "business.gov.au (Australian Government)",
      scope: "National",
      type: "Directory",
      status: "Official directory",
      url: "https://business.gov.au/grants-and-programs",
    },
    {
      name: "GrantConnect",
      provider: "Australian Government",
      scope: "National",
      type: "Directory",
      status: "Official directory",
      url: "https://www.grants.gov.au/",
    },
    {
      name: "Social Enterprise Development Initiative (SEDI)",
      provider: "Department of Social Services",
      scope: "National",
      type: "Capability support",
      status: "Programme information - check current round",
      url: "https://www.dss.gov.au/social-impact-investing/social-enterprise-development-initiative",
    },
    {
      name: "Indigenous Business Australia",
      provider: "Indigenous Business Australia",
      scope: "National",
      type: "Finance and business support",
      status: "Programme information - check current availability",
      url: "https://iba.gov.au/",
    },
    {
      name: "Australian Business Licence and Information Service (ABLIS)",
      provider: "business.gov.au (Australian Government)",
      scope: "National",
      type: "Directory",
      status: "Official directory",
      url: "https://ablis.business.gov.au/",
    },
  ],
};

// --- Section 10: financial planning tools -------------------------------------

export const cfeToolsNote =
  "These tools run in your browser only. Nothing is sent or stored on a server. Outputs are indicative planning aids, not financial advice or a grant eligibility result.";

export const cfeBreakEven = {
  heading: "Operating break-even calculator",
  defaults: { fixed: 6000, price: 35, variable: 23, subsidy: 0 },
  note: "Illustrative example. Include fair labour, waste, packaging and delivery in your costs, and avoid double-counting per-unit labour in fixed costs. Use consistent GST treatment.",
  limits:
    "This model does not include tax, loan principal, start-up capital, inventory timing, seasonal demand or capacity. A capital grant is not operating subsidy unless it legitimately funds that period's operations.",
};

export const cfeFundingGap = {
  heading: "Start-up funding gap calculator",
  note: "In-kind support is not cash, and pending grant applications are not confirmed funds. Restricted funds may only offset eligible costs.",
};

export const cfeChecklist = {
  heading: "Readiness checklist",
  intro: "A planning aid, not an investment-readiness score or guarantee. Your answers stay in this browser; you can reset them at any time.",
  items: [
    "Community need is evidenced",
    "Community authority and mandate are clear",
    "Customer evidence collected",
    "Initial offer chosen",
    "Governance roles and decision process agreed",
    "Entity or auspice pathway identified",
    "Permits and licensing pathway mapped",
    "Safe, permissible premises identified",
    "Workforce plan drafted",
    "Budget with quotes prepared",
    "Monthly cash-flow forecast prepared",
    "Funding mix identified with gap stated",
    "Delivery responsibilities assigned",
    "Outcome measures agreed",
    "Pilot review and exit criteria agreed",
  ],
};

// --- Section 11: illustrative scenarios ---------------------------------------

export const cfeScenarios = {
  label: "Illustrative scenarios - numbers are assumptions, not costs or results of real projects",
  items: [
    {
      title: "Small buying group",
      body: "Pilot a collection cycle with customer orders before extending storage or committing to a premises. Test order accuracy, repeat demand, volunteer coordination and the handling margin.",
      test: "Test: one pre-ordered collection cycle.",
      funding: "Possible funding mix: member pre-orders plus a small local grant.",
      decision: "Decision before expanding: repeat demand and a workable handling margin.",
    },
    {
      title: "Community kitchen",
      body: "Cost each meal including preparation, cleaning, packaging and delivery. Separate commercial catering from any subsidised meal service so each activity's funding requirement is visible.",
      test: "Test: a costed menu from an appropriately approved venue.",
      funding: "Possible funding mix: catering income plus a committed service agreement for subsidised meals.",
      decision: "Decision before expanding: each activity's true cost and funding source.",
    },
    {
      title: "Regional grower hub",
      body: "Obtain buyer interest and supply evidence, then test a small delivery run. Model spoilage, cold chain, freight and the gap between paying growers and receiving buyer payments.",
      test: "Test: a small delivery run with confirmed buyers.",
      funding: "Possible funding mix: sales margin plus working capital for the payment gap.",
      decision: "Decision before expanding: reliable supply and survivable payment terms.",
    },
    {
      title: "First Nations-led food project",
      body: "Begin with the relevant community's mandate, cultural governance and permitted knowledge use; develop the commercial pathway within those decisions. Incorporation or investment is not a substitute for cultural authority.",
      test: "Test: a community-authorised pilot with agreed benefit arrangements.",
      funding: "Possible funding mix: appropriate grants or patient finance under community authority.",
      decision: "Decision before expanding: community authority and protected knowledge use.",
    },
  ],
};

// --- Section 12: outcomes and accountability -----------------------------------

export const cfeOutcomes = {
  intro:
    "A small balanced set of indicators. Distinguish outputs from outcomes, avoid promising causation, use consent and proportionate data collection, and make reporting useful to the community, not just the funder. Do not require disclosure of sensitive household information to demonstrate benefit.",
  groups: [
    {
      title: "Food access",
      text: "Reach, affordability compared with a relevant local baseline, suitability and participant feedback.",
    },
    {
      title: "Local livelihoods",
      text: "Paid hours, job quality, producer payments and supplier retention.",
    },
    {
      title: "Community power",
      text: "Participation, representation and evidence of decisions influenced by members.",
    },
    {
      title: "Financial resilience",
      text: "Contribution margin, unrestricted cash, cash-flow forecast, repeat sales and funding concentration.",
    },
    {
      title: "Operations",
      text: "Waste, stock loss, safety incidents and delivery reliability.",
    },
    {
      title: "Ecological outcomes",
      text: "Specific supported changes, measured - not vague 'regenerative' claims.",
    },
  ],
};

// --- Section 13: FAQs -----------------------------------------------------------

export const cfeFaqs = [
  {
    q: "Is every community food project a social enterprise?",
    a: "No. A volunteer mutual-aid activity, community garden or subsidised service may be the right model. Social enterprise describes trading for a purpose; it suits some projects and not others. Choose deliberately, based on who benefits, who decides and how costs are covered.",
  },
  {
    q: "Do we need to incorporate before testing an idea?",
    a: "Not always. A small, lawful pilot can sometimes run under an existing organisation as a host. Incorporation creates obligations as well as protections - match the timing to the risk, the money involved and the structure you intend.",
  },
  {
    q: "Can an existing organisation auspice the project?",
    a: "Often, yes. Agree in writing who controls the project, funds, fees, employment, insurance, assets, reporting, liabilities and exit. The host's eligibility for funding does not automatically transfer to the project.",
  },
  {
    q: "What is the difference between a co-operative and a not-for-profit?",
    a: "A co-operative is a member-owned structure registered under co-operatives law, with democratic member participation. Not-for-profit describes how surplus is treated, not a single structure - associations, companies limited by guarantee and co-operatives can all be not-for-profit.",
  },
  {
    q: "Can a not-for-profit trade and pay staff?",
    a: "Yes. Not-for-profit organisations can trade, earn income and pay fair wages; the constraint is that surplus supports the purpose rather than being distributed to members. Wage and award obligations apply as to any employer.",
  },
  {
    q: "Does charity registration make donations tax-deductible?",
    a: "No. Deductible gift recipient (DGR) endorsement is separate from charity registration and applies only to certain purposes and funds. Do not promise donors tax deductibility unless the relevant endorsement is in place.",
  },
  {
    q: "Do community groups need food licences or notification?",
    a: "Usually some form of notification, registration or licensing applies, varying by state, territory, local government and activity. Selling food generally triggers obligations even for community groups. Check with your local council and state or territory food regulator before trading.",
  },
  {
    q: "Can we use volunteers instead of employees?",
    a: "Sometimes, but volunteering has rules. A community purpose does not remove wage obligations where an employment relationship exists, and arrangements must be assessed on their facts. Fair Work guidance on unpaid work is the starting point.",
  },
  {
    q: "Can we operate from a shared kitchen or community hall?",
    a: "Often, with the venue's written permission and the right regulatory approvals. Confirm who holds the food safety registration or licence, whether you may operate independently, and how insurance, access, cleaning and storage are allocated.",
  },
  {
    q: "Can grants pay ongoing wages and rent?",
    a: "Some can, many cannot. Grants are often restricted to defined costs and time-limited. Treat each grant's eligible costs as written, and plan for what happens when the grant ends before committing to ongoing expenses.",
  },
  {
    q: "When is borrowing appropriate?",
    a: "When there is credible repayment capacity - tested cash flow, a downside case and a clear plan. Interest, fees, security and guarantees are real obligations. Borrowing to cover a structural operating loss usually deepens the problem rather than solving it.",
  },
  {
    q: "How do we keep community control as funding grows?",
    a: "Design for it early: membership and voting rules, asset locks where available, limits on investor rights, board accountability to members and transparent reporting. Each new funding source should be checked against the ownership and control you intend to keep.",
  },
  {
    q: "How should First Nations foods, seeds and knowledge be protected?",
    a: "Authority and consent come from the appropriate custodians. Agree data governance, recording, branding, provenance, benefit sharing and limits on disclosure before commercial activity. Registering a seed or variety does not by itself create ownership of a species or prevent biopiracy.",
  },
  {
    q: "What if sales do not cover the price people can afford?",
    a: "Name the gap honestly. Options include a committed ongoing subsidy, cross-subsidy from other trading, cost redesign, or choosing a service model rather than a self-financing enterprise. Not all activities can become self-financing - an openly funded service can be the responsible answer.",
  },
];

// --- Section 14: official resources and jurisdictions ---------------------------

export const cfeNationalResources = [
  { name: "Grants and programs finder", provider: "business.gov.au", url: "https://business.gov.au/grants-and-programs" },
  { name: "GrantConnect - Commonwealth grants", provider: "Australian Government", url: "https://www.grants.gov.au/" },
  { name: "Grants and support", provider: "business.gov.au", url: "https://business.gov.au/grants-and-support" },
  { name: "Australian Business Licence and Information Service (ABLIS)", provider: "business.gov.au", url: "https://ablis.business.gov.au/" },
  { name: "Register licences and permits", provider: "business.gov.au", url: "https://business.gov.au/registrations/register-licences-and-permits" },
  { name: "Australian business structures", provider: "business.gov.au", url: "https://business.gov.au/planning/business-structures-and-types" },
  { name: "Starting or changing a food business", provider: "Food Standards Australia New Zealand", url: "https://www.foodstandards.gov.au/business/food-safety/starting-food-business" },
  { name: "Food safety for charities and community organisations", provider: "Food Standards Australia New Zealand", url: "https://www.foodstandards.gov.au/business/charities" },
  { name: "Legal structures for charities", provider: "ACNC", url: "https://www.acnc.gov.au/for-charities/start-charity/you-start-charity/legal-structure" },
  { name: "Deductible gift recipients (DGR) explained", provider: "ACNC", url: "https://www.acnc.gov.au/tools/factsheets/deductible-gift-recipients-dgr-and-acnc" },
  { name: "Unpaid work guidance", provider: "Fair Work Ombudsman", url: "https://www.fairwork.gov.au/starting-employment/unpaid-work" },
  { name: "Social Enterprise Development Initiative", provider: "Department of Social Services", url: "https://www.dss.gov.au/social-impact-investing/social-enterprise-development-initiative" },
  { name: "Australian Taxation Office", provider: "ATO", url: "https://www.ato.gov.au/" },
  { name: "Australian Securities and Investments Commission", provider: "ASIC", url: "https://www.asic.gov.au/" },
  { name: "Office of the Registrar of Indigenous Corporations", provider: "ORIC", url: "https://www.oric.gov.au/" },
  { name: "Indigenous Business Australia", provider: "IBA", url: "https://iba.gov.au/" },
  { name: "Safe Work Australia", provider: "Safe Work Australia", url: "https://www.safeworkaustralia.gov.au/" },
];

// State/territory selector. Stable official portals only; where a specific
// programme page cannot be verified, the jurisdiction's main official portal
// is used and ABLIS remains the national fallback.
export const cfeJurisdictions = [
  {
    id: "ACT",
    food: { label: "Access Canberra - food business registration", url: "https://www.accesscanberra.act.gov.au/" },
    business: { label: "ACT Government - business support", url: "https://www.act.gov.au/business" },
    entity: { label: "Access Canberra - associations and co-operatives", url: "https://www.accesscanberra.act.gov.au/" },
  },
  {
    id: "NSW",
    food: { label: "NSW Food Authority", url: "https://www.foodauthority.nsw.gov.au/" },
    business: { label: "NSW Government - grants and funding", url: "https://www.nsw.gov.au/grants-and-funding" },
    entity: { label: "NSW Fair Trading - associations and co-operatives", url: "https://www.fairtrading.nsw.gov.au/" },
  },
  {
    id: "NT",
    food: { label: "NT Government - health and community wellbeing", url: "https://nt.gov.au/wellbeing" },
    business: { label: "NT Government - business", url: "https://nt.gov.au/industry" },
    entity: { label: "NT Government - associations and co-operatives", url: "https://nt.gov.au/" },
  },
  {
    id: "QLD",
    food: { label: "Queensland Health - food safety", url: "https://www.health.qld.gov.au/" },
    business: { label: "Business Queensland", url: "https://www.business.qld.gov.au/" },
    entity: { label: "Queensland Government - incorporated associations and co-operatives", url: "https://www.qld.gov.au/law" },
  },
  {
    id: "SA",
    food: { label: "SA Health - food safety", url: "https://www.sahealth.sa.gov.au/" },
    business: { label: "South Australian business portal", url: "https://business.sa.gov.au/" },
    entity: { label: "Consumer and Business Services SA - associations and co-operatives", url: "https://www.cbs.sa.gov.au/" },
  },
  {
    id: "TAS",
    food: { label: "Tasmanian Department of Health - food safety", url: "https://www.health.tas.gov.au/" },
    business: { label: "Business Tasmania", url: "https://www.business.tas.gov.au/" },
    entity: { label: "Consumer, Building and Occupational Services - associations and co-operatives", url: "https://www.cbos.tas.gov.au/" },
  },
  {
    id: "VIC",
    food: { label: "Victorian Department of Health - food safety", url: "https://www.health.vic.gov.au/food-safety" },
    business: { label: "Business Victoria - grants and programs", url: "https://business.vic.gov.au/grants-and-programs" },
    entity: { label: "Consumer Affairs Victoria - clubs and associations", url: "https://www.consumer.vic.gov.au/" },
  },
  {
    id: "WA",
    food: { label: "WA Department of Health", url: "https://www.health.wa.gov.au/" },
    business: { label: "Small Business Development Corporation WA", url: "https://www.smallbusiness.wa.gov.au/" },
    entity: { label: "Department of Mines, Industry Regulation and Safety - associations and co-operatives", url: "https://www.commerce.wa.gov.au/" },
  },
];

// --- Section 15: closing CTA ----------------------------------------------------

export const cfeClosing = {
  heading: "Start with one credible next step.",
  body: "Define the community need, choose a small test and identify the people, permissions and funding needed to deliver it responsibly.",
  ctas: [
    { label: "Review the readiness checklist", anchor: "readiness" },
    { label: "Print this guide", action: "print" },
    { label: "Explore funding resources", anchor: "funding" },
  ],
  contactNote: {
    before: "Developing a community food enterprise in a particular place? ",
    linkText: "Discuss it with us",
    href: "/contact",
  },
};

// --- Card shown in Our Work → Active projects ----------------------------------

export const cfeProjectCard = {
  name: "Community Food Enterprises",
  tagline:
    "Explore practical pathways to establish, fund and sustain community-owned or community-led food enterprises - from food co-operatives and market gardens to community kitchens and local distribution hubs.",
  status: "Educational resource",
  href: "/our-work/community-food-enterprise",
  ctaLabel: "Explore the guide",
  image: {
    src: "/about-locally-grown.jpg",
    alt: "Fresh locally grown produce at a community market.",
  },
};
